import { ProductSegment } from 'constants/product/productTypes'
import { PropertySurfaceDTO } from 'models/property'
import { logger } from 'utils/logging'

/** Square-meters per square-foot, for SQFT → m² conversion; the backend's factor. */
const SQFT_TO_SQM = 0.09290304

/**
 * Area bands in ascending order. Each surface (in m²) falls into the first band whose lower bound it
 * meets: `[0,100) → m0000_0099`, `[100,200) → m0100_0199`, …, `[900,1000) → m0900_0999`, `>= 1000 → m1000_p`.
 */
const AREA_BANDS: ReadonlyArray<{ minSqm: number, segment: ProductSegment }> = [
  { minSqm: 1000, segment: ProductSegment.m1000_p },
  { minSqm: 900, segment: ProductSegment.m0900_0999 },
  { minSqm: 800, segment: ProductSegment.m0800_0899 },
  { minSqm: 700, segment: ProductSegment.m0700_0799 },
  { minSqm: 600, segment: ProductSegment.m0600_0699 },
  { minSqm: 500, segment: ProductSegment.m0500_0599 },
  { minSqm: 400, segment: ProductSegment.m0400_0499 },
  { minSqm: 300, segment: ProductSegment.m0300_0399 },
  { minSqm: 200, segment: ProductSegment.m0200_0299 },
  { minSqm: 100, segment: ProductSegment.m0100_0199 },
  { minSqm: 0, segment: ProductSegment.m0000_0099 },
]

/**
 * Maps a property surface to the matching area `ProductSegment` band, converting SQFT to m² and rounding to the
 * whole m² first, exactly as the backend does.
 * Returns null when the surface is missing or non-positive (the caller then shows the manual gate).
 * Only ever returns an area band (never `all` or a duration segment).
 */
export function segmentForSurface(surface: PropertySurfaceDTO | undefined): ProductSegment | null {
  if (!surface) return null

  const sqm = surface.unit === 'SQFT' ? surface.value * SQFT_TO_SQM : surface.value
  if (!(sqm > 0)) return null

  // Banded on the whole m², rounded half-up, as the backend bands it (RealEstatePropertyExtension.areaInSquareMeters):
  // a 99.5 m² property is priced in 100-199 there, so it must be shown in 100-199 here.
  const wholeSqm = Math.round(sqm)
  return AREA_BANDS.find(({ minSqm }) => wholeSqm >= minSqm)?.segment ?? ProductSegment.m0000_0099
}

/** The product area bands a property can be priced in, ascending: the options of the drawer's range select. */
export const AREA_RANGES: ReadonlyArray<ProductSegment> = [...AREA_BANDS].reverse().map(({ segment }) => segment)

const reportedUnrecognisedRanges = new Set<string>()

/**
 * The area band a property is priced in, as the backend reads it: the band of its exact surface, else the range
 * the client picked. Null when it has neither.
 */
export function areaRangeOf(property: { surface?: PropertySurfaceDTO, areaRange?: ProductSegment } | undefined): ProductSegment | null {
  if (!property) return null
  const fromSurface = segmentForSurface(property.surface)
  if (fromSurface) return fromSurface
  if (!property.areaRange) return null
  if (AREA_RANGES.includes(property.areaRange)) return property.areaRange
  // The backend only stores area bands, so another value means the two sides disagree, not a missing area.
  // Reported once per value: the check runs on every render of the property.
  if (reportedUnrecognisedRanges.has(property.areaRange)) return null
  reportedUnrecognisedRanges.add(property.areaRange)
  logger.warn('PF revamp: property carries an area range that is not an area band', undefined, {
    context: { component: 'segmentForSurface', action: 'area_range_unrecognised', metadata: { areaRange: property.areaRange } },
  })
  return null
}

/** Why the embed area gate was shown, as the closed `reason` of `embed_size_gate_blocked` (ISSUE-4022). */
export type SizeGateReason = 'surface_missing' | 'surface_unmappable' | 'band_unavailable'

/**
 * Why the embed auto-select gave up and the manual gate is showing: no surface on the property at all, a
 * surface that maps to no band (a non-positive value; a unit other than SQFT is read as m²), or a mapped band the catalogue does not
 * offer. The gate only ever renders for one of these three, so a mappable surface implies the last.
 */
export function sizeGateReason(surface: PropertySurfaceDTO | undefined): SizeGateReason {
  if (!surface) return 'surface_missing'
  if (segmentForSurface(surface) === null) return 'surface_unmappable'
  return 'band_unavailable'
}
